/* istanbul ignore file - better tested visually */

import { ComponentProps } from 'react';
import { Controller, ControllerProps, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField, FormFieldLabel, FormFieldMessage } from '../FormField/FormField';

export type ReactHookFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id?: string;
  label?: string;
} & ControllerProps<TFieldValues, TName> &
  ComponentProps<typeof FormField>;

export function ReactHookFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  label,
  render,
  control,
  defaultValue,
  disabled,
  rules,
  shouldUnregister,
  key,
  ...rest
}: ReactHookFormFieldProps<TFieldValues, TName>) {
  const { t: i18n } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      disabled={disabled}
      rules={rules}
      shouldUnregister={shouldUnregister}
      key={key}
      render={(args) => {
        const {
          fieldState: { error },
        } = args;

        return (
          <FormField {...rest}>
            {label && <FormFieldLabel htmlFor={id}>{label}</FormFieldLabel>}
            {render(args)}
            <FormFieldMessage type="error" show={error != null}>
              {i18n(error?.message ?? '')}
            </FormFieldMessage>
          </FormField>
        );
      }}
    />
  );
}
